import React from 'react'

import { Chip, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import DescriptionIcon from '@material-ui/icons/Description'
import LinkIcon from '@material-ui/icons/Link'
import CategoryIcon from '@material-ui/icons/Category'

import { LoriIcon } from '../../../navigation/layouts/Lori/Icons/LoriIcon'

import { Container } from '../../../components'
import { getProductProfileLabel, ProductProfileListItem } from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    profile: ProductProfileListItem
    onClick: (profile: ProductProfileListItem) => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            padding: 0,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 14,
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
                borderColor: theme.palette.primary.light,
                boxShadow: `0 8px 24px rgba(0,0,0,0.06), 0 0 0 1px ${theme.palette.primary.main}18`,
                transform: 'translateY(-2px)',
            },
            '&:hover $chevron': {
                transform: 'translateX(3px)',
                color: theme.palette.primary.main,
            },
        },
        inner: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2, 2.5),
        },
        iconBox: {
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        chevron: {
            transition: 'all 0.2s ease',
            color: theme.palette.grey[400],
            fontSize: 22,
        },
    }),
)

export const ProductProfileRow: React.FC<Props> = ({ profile, onClick }) => {
    const theme = useTheme()
    const classes = useStyles()

    return (
        <Container className={classes.card} onClick={() => onClick(profile)}>
            <div className={classes.inner}>
                <div className={classes.iconBox}>
                    <CategoryIcon
                        style={{
                            fontSize: 22,
                            color: theme.palette.primary.main,
                        }}
                    />
                </div>

                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 700,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                        }}
                    >
                        {getProductProfileLabel(profile)}
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                        }}
                    >
                        {profile.brand} · {profile.model_number}
                    </span>
                </Container>

                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        flexShrink: 0,
                    }}
                >
                    <Chip
                        size="small"
                        icon={
                            <DescriptionIcon
                                style={{
                                    fontSize: 13,
                                    color: theme.palette.info.main,
                                }}
                            />
                        }
                        label={`${profile.document_count} ${
                            profile.document_count === 1 ? 'doc' : 'docs'
                        }`}
                        style={{
                            backgroundColor: `${theme.palette.info.main}10`,
                            color: theme.palette.info.main,
                            fontWeight: 600,
                            fontSize: 11,
                            height: 24,
                            border: `1px solid ${theme.palette.info.main}25`,
                        }}
                    />
                    {profile.suggested_document_count > 0 && (
                        <Chip
                            size="small"
                            icon={<LoriIcon width={13} height={13} />}
                            label={`${profile.suggested_document_count} Lori`}
                            style={{
                                backgroundColor: `${theme.palette.warning.main}10`,
                                color: theme.palette.warning.dark,
                                fontWeight: 600,
                                fontSize: 11,
                                height: 24,
                                border: `1px solid ${theme.palette.warning.main}25`,
                            }}
                        />
                    )}
                    <Chip
                        size="small"
                        icon={
                            <LinkIcon
                                style={{
                                    fontSize: 13,
                                    color: theme.palette.success.main,
                                }}
                            />
                        }
                        label={`${profile.asset_count} ${
                            profile.asset_count === 1 ? 'unit' : 'units'
                        }`}
                        style={{
                            backgroundColor: `${theme.palette.success.main}10`,
                            color: theme.palette.success.main,
                            fontWeight: 600,
                            fontSize: 11,
                            height: 24,
                            border: `1px solid ${theme.palette.success.main}25`,
                        }}
                    />
                    <ChevronRightIcon className={classes.chevron} />
                </Container>
            </div>
        </Container>
    )
}
